/**
 * EditorPage: the core screen (PRD §14.4). Loads a wedding + its layout
 * (or routes into the generation flow if no layout exists yet), and
 * assembles the canvas + library panel + inspector panel + live counters
 * bar into the responsive layout described in useResponsiveLayout.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWedding, useCreateWedding } from '../api/weddings';
import { useLayout, useCreateLayout, useSaveLayout, useDuplicateLayout } from '../api/layouts';
import { useRoomDefinitions } from '../api/roomDefinitions';
import { useObjectCatalog, useInventoryLimits } from '../api/inventoryLimits';
import { useCreateShareLink, buildShareUrl } from '../api/shareLinks';
import { useAuth } from '../auth/AuthProvider';
import { useEditorStore } from '../store/useEditorStore';
import { useAutosave } from '../hooks/useAutosave';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { generateLayout } from '../domain/generation/generateLayout';
import type { ValidationContext } from '../domain/types';
import { PlannerCanvas, type RoomOrientation } from '../components/canvas/PlannerCanvas';
import { ObjectLibraryPanel } from '../components/panels/ObjectLibraryPanel';
import { InspectorPanel } from '../components/panels/InspectorPanel';
import { LiveCountersBar } from '../components/panels/LiveCountersBar';
import { Toast, ToastStack } from '../components/shared/Toast';

export function EditorPage() {
  const { weddingId } = useParams<{ weddingId: string }>();
  const navigate = useNavigate();
  const layoutMode = useResponsiveLayout();

  const { data: wedding, isLoading: weddingLoading } = useWedding(weddingId);
  const { data: layout, isLoading: layoutLoading } = useLayout(weddingId);
  const { data: roomDefinitions } = useRoomDefinitions();
  const { data: catalog } = useObjectCatalog();
  const { data: inventoryLimits } = useInventoryLimits();

  const createLayout = useCreateLayout();
  const saveLayout = useSaveLayout();
  const createShareLink = useCreateShareLink();
  const createWeddingForTemplate = useCreateWedding();
  const duplicateLayoutForTemplate = useDuplicateLayout();
  const { profile } = useAuth();

  const loadLayoutIntoStore = useEditorStore((state) => state.loadLayout);
  const showWarningsOnSetupSheet = useEditorStore((state) => state.showWarningsOnSetupSheet);
  const setShowWarningsOnSetupSheet = useEditorStore((state) => state.setShowWarningsOnSetupSheet);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const canUndo = useEditorStore((state) => state.canUndo);
  const canRedo = useEditorStore((state) => state.canRedo);
  const selectAll = useEditorStore((state) => state.selectAll);
  const clearSelection = useEditorStore((state) => state.clearSelection);
  const selectedCount = useEditorStore((state) => state.selectedIds.length);

  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [orientation, setOrientation] = useState<RoomOrientation>('rotated');
  const [saveToast, setSaveToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const hasLoadedRef = useRef(false);

  const autosaveState = useAutosave(weddingId);
  useEditorShortcuts();

  // Surface autosave FAILURES only, never routine successes — autosave is
  // meant to be invisible when it works (per the original design), but a
  // silent failure could mean a coordinator loses real edits without ever
  // knowing. Manual Save (handleSaveNow below) always gives feedback
  // either way, since that's an explicit user action.
  const lastAutosaveErrorRef = useRef<unknown>(null);
  useEffect(() => {
    if (autosaveState.lastError && autosaveState.lastError !== lastAutosaveErrorRef.current) {
      lastAutosaveErrorRef.current = autosaveState.lastError;
      setSaveToast({ message: 'Auto-save failed \u2014 use Save to retry.', tone: 'error' });
    }
  }, [autosaveState.lastError]);

  // Full-screen/focus mode exit via Escape — a coordinator showing a
  // bride a layout on a laptop needs a fast, obvious way back, and
  // Escape is the universal convention for "get me out of focus mode."
  useEffect(() => {
    if (!isFullScreen) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsFullScreen(false);
    }
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullScreen]);

  const roomDefinition = useMemo(
    () => roomDefinitions?.find((r) => r.room === wedding?.room),
    [roomDefinitions, wedding]
  );

  const validationContext: ValidationContext | null = useMemo(() => {
    if (!wedding || !roomDefinition || !catalog || !inventoryLimits) return null;
    return {
      guestCount: wedding.guestCount,
      roomDefinition,
      objectCatalog: catalog,
      inventoryLimits,
      layoutStyle: wedding.layoutStyle,
    };
  }, [wedding, roomDefinition, catalog, inventoryLimits]);

  // Load existing layout into the editor store once all data is ready.
  // Guarded by hasLoadedRef so we don't re-run loadLayout (and thus reset
  // undo history) on every unrelated re-render — only on a genuine first
  // load for this wedding.
  useEffect(() => {
    if (hasLoadedRef.current) return;
    if (!layout || !validationContext) return;
    loadLayoutIntoStore(layout.objects, validationContext, layout.showWarningsOnSetupSheet);
    hasLoadedRef.current = true;
  }, [layout, validationContext, loadLayoutIntoStore]);

  // Reset the "has loaded" guard when navigating to a different wedding.
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [weddingId]);

  async function handleGenerateLayout() {
    if (!wedding || !roomDefinition || !catalog || !inventoryLimits) return;

    try {
      const result = generateLayout({
        room: wedding.room,
        guestCount: wedding.guestCount,
        headTableType: wedding.headTableType,
        headTablePartySize: wedding.headTablePartySize,
        layoutStyle: wedding.layoutStyle,
        roomDefinition,
        objectCatalog: catalog,
        inventoryLimits,
      });

      await createLayout.mutateAsync({
        weddingId: wedding.id,
        objects: result.objects,
        generatedAt: new Date().toISOString(),
      });

      if (validationContext) {
        loadLayoutIntoStore(result.objects, validationContext);
        hasLoadedRef.current = true;
      }
    } catch {
      setSaveToast({
        message: "Couldn't generate the layout \u2014 please check your connection and try again.",
        tone: 'error',
      });
    }
  }

  async function handleStartBlank() {
    if (!wedding) return;
    try {
      await createLayout.mutateAsync({
        weddingId: wedding.id,
        objects: [],
        generatedAt: new Date().toISOString(),
      });
      if (validationContext) {
        loadLayoutIntoStore([], validationContext);
        hasLoadedRef.current = true;
      }
    } catch {
      setSaveToast({
        message: "Couldn't create the layout \u2014 please check your connection and try again.",
        tone: 'error',
      });
    }
  }

  async function handleSaveNow() {
    if (!weddingId) return;
    const objects = useEditorStore.getState().objects;
    try {
      const { objects: objs, showWarningsOnSetupSheet: showW } = useEditorStore.getState();
      await saveLayout.mutateAsync({ weddingId, objects: objs, showWarningsOnSetupSheet: showW });
      setSaveToast({ message: 'Layout saved.', tone: 'success' });
    } catch {
      setSaveToast({ message: 'Save failed \u2014 please try again.', tone: 'error' });
    }
  }


  /**
   * Save as Template: creates an INDEPENDENT new wedding record with
   * isTemplateReference=true, then copies the layout graph to it.
   * The original wedding/layout is NEVER modified.
   * Uses the same safe two-step clone pattern as TemplateLibraryPage.
   */
  async function handleSaveAsTemplate() {
    if (!wedding || !weddingId || !profile) return;
    setIsSavingTemplate(true);
    try {
      const roomLabel = wedding.room.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
      const templateWedding = await createWeddingForTemplate.mutateAsync({
        coupleNames: `Template — ${roomLabel}`,
        eventDate: new Date().toISOString().slice(0, 10),
        room: wedding.room,
        guestCount: wedding.guestCount,
        headTableType: wedding.headTableType,
        headTablePartySize: wedding.headTablePartySize,
        layoutStyle: wedding.layoutStyle,
        createdBy: profile.id,
        isTemplateReference: true,
      });
      await duplicateLayoutForTemplate.mutateAsync({
        sourceWeddingId: weddingId,
        targetWeddingId: templateWedding.id,
      });
      setSaveToast({ message: 'Saved as a reusable template.', tone: 'success' });
    } catch {
      setSaveToast({ message: "Couldn’t save as template — please try again.", tone: 'error' });
    } finally {
      setIsSavingTemplate(false);
    }
  }

  async function handleCreateShareLink() {
    if (!weddingId) return;
    try {
      const token = await createShareLink.mutateAsync(weddingId);
      setShareUrl(buildShareUrl(token));
    } catch {
      setSaveToast({
        message: "Couldn't create a share link \u2014 please try again.",
        tone: 'error',
      });
    }
  }

  if (weddingLoading || layoutLoading || !catalog || !roomDefinitions || !inventoryLimits) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center">
        <p className="font-body text-ink-400">Loading…</p>
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="flex h-[calc(100vh-56px)] flex-col items-center justify-center gap-3">
        <p className="font-body text-ink-500">This wedding couldn&rsquo;t be found.</p>
        <button onClick={() => navigate('/')} className="font-body text-evergreen-600 underline">
          Back to dashboard
        </button>
      </div>
    );
  }

  if (!roomDefinition) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center">
        <p className="font-body text-ink-500">
          No room data found for &ldquo;{wedding.room}&rdquo;. Ask an admin to check Venue Spaces.
        </p>
      </div>
    );
  }

  // No layout exists yet for this wedding — show both starting-point
  // options instead of forcing generation. This screen is now a genuine
  // fallback (the wizard creates the layout immediately in the normal
  // path) rather than the only way to get a layout, reached only if
  // room/catalog data hadn't finished loading at the moment the wizard
  // submitted.
  if (!layout) {
    return (
      <div className="flex h-[calc(100vh-56px)] flex-col items-center justify-center gap-4 px-6 text-center">
        <h2 className="font-display text-2xl text-ink-700">
          Ready to build {wedding.coupleNames}&rsquo;s layout
        </h2>
        <p className="max-w-md font-body text-sm text-ink-500">
          {wedding.guestCount} guests in {roomDefinition.displayName}. Generate a complete
          starting layout, or start from an empty room and build it yourself.
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={handleStartBlank}
            disabled={createLayout.isPending}
            className="rounded border border-evergreen-300 px-6 py-3 font-body font-medium text-evergreen-700 hover:bg-evergreen-50 disabled:opacity-60"
          >
            {createLayout.isPending ? 'Creating\u2026' : '\u270F\uFE0F Start Blank'}
          </button>
          <button
            onClick={handleGenerateLayout}
            disabled={createLayout.isPending}
            className="rounded bg-evergreen-600 px-6 py-3 font-body font-medium text-ivory-50 hover:bg-evergreen-700 disabled:opacity-60"
          >
            {createLayout.isPending ? 'Generating\u2026' : '\u2728 Generate My Layout'}
          </button>
        </div>

        {/* This early-return screen needs its own ToastStack — it's a
            separate render path from the main editor view below, which
            has its own ToastStack at the very end. Without this, a
            generation failure on a brand-new wedding's very first screen
            (arguably the single most important moment to get error
            feedback right) would silently update saveToast state with
            nothing on screen to ever display it. */}
        <ToastStack>
          {saveToast && (
            <Toast
              message={saveToast.message}
              tone={saveToast.tone}
              onDismiss={() => setSaveToast(null)}
            />
          )}
        </ToastStack>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col">
      {!isFullScreen && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ivory-300 bg-white px-4 py-2.5">
          <div className="min-w-0">
            <h1 className="truncate font-display text-lg text-ink-700">{wedding.coupleNames}</h1>
            <p className="font-body text-xs text-ink-400">
              {roomDefinition.displayName} {'\u00b7'} {wedding.guestCount} guests
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Edit history — compact, icon-only, low visual weight since
                these are frequent micro-actions, not decisions. */}
            <div className="flex items-center gap-0.5 rounded-lg border border-ivory-300 p-0.5">
              <button
                onClick={undo}
                disabled={!canUndo()}
                aria-label="Undo"
                title="Undo (Ctrl/Cmd+Z)"
                className="flex h-8 w-8 items-center justify-center rounded font-body text-base text-ink-600 hover:bg-ivory-100 disabled:opacity-30"
              >
                {'\u21b6'}
              </button>
              <button
                onClick={redo}
                disabled={!canRedo()}
                aria-label="Redo"
                title="Redo (Ctrl/Cmd+Shift+Z)"
                className="flex h-8 w-8 items-center justify-center rounded font-body text-base text-ink-600 hover:bg-ivory-100 disabled:opacity-30"
              >
                {'\u21b7'}
              </button>
            </div>

            {/* Selection — select everything at once (for a group lock/
                delete/duplicate via the Inspector panel), or clear the
                current selection. A visible entry point matters here:
                Ctrl/Cmd+A also works, but a coordinator shouldn't need
                to know that to use this. */}
            <div className="flex items-center gap-0.5 rounded-lg border border-ivory-300 p-0.5">
              <button
                onClick={selectAll}
                title="Select everything in the room (Ctrl/Cmd+A)"
                className="flex h-8 items-center gap-1.5 rounded px-2 font-body text-sm text-ink-600 hover:bg-ivory-100"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                disabled={selectedCount === 0}
                title="Clear selection"
                className="flex h-8 items-center gap-1.5 rounded px-2 font-body text-sm text-ink-600 hover:bg-ivory-100 disabled:opacity-30"
              >
                Clear
              </button>
            </div>

            {/* View controls — how you're LOOKING at the room, not the
                data itself. "Rotate room view" instead of "Landscape /
                Portrait", which is printer-paper jargon a coordinator
                has no reason to know. */}
            <div className="flex items-center gap-0.5 rounded-lg border border-ivory-300 p-0.5">
              <button
                onClick={() => setOrientation((o) => (o === 'standard' ? 'rotated' : 'standard'))}
                title={'Rotate room view (just changes how you see it here \u2014 never affects what gets saved or printed)'}
                className="flex h-8 items-center gap-1.5 rounded px-2 font-body text-sm text-ink-600 hover:bg-ivory-100"
              >
                <span aria-hidden>{'\u21bb'}</span>
                Rotate view
              </button>
              <button
                onClick={() => setIsFullScreen(true)}
                title={'Full screen \u2014 hide panels to focus on the layout'}
                className="flex h-8 items-center gap-1.5 rounded px-2 font-body text-sm text-ink-600 hover:bg-ivory-100"
              >
                <span aria-hidden>{'\u26F6'}</span>
                Full screen
              </button>
            </div>

            <div className="h-7 w-px bg-ivory-200" />

            {/* Primary actions — Save is the safe, frequent default
                (outlined, calm). Export and Share are the two "I'm
                ready to hand this off" moments and get the most visual
                weight, in the order a coordinator actually uses them:
                export for staff, then share with the couple. */}
            <button
              onClick={handleSaveNow}
              disabled={saveLayout.isPending}
              className="rounded-lg border border-evergreen-300 px-3 py-1.5 font-body text-sm text-evergreen-700 hover:bg-evergreen-50 disabled:opacity-60"
            >
              {saveLayout.isPending ? 'Saving\u2026' : 'Save'}
            </button>
            <button
              onClick={() => navigate(`/weddings/${weddingId}/setup-sheet`)}
              title="Print or download a setup sheet for your staff"
              className="rounded-lg border border-evergreen-600 bg-evergreen-600 px-3 py-1.5 font-body text-sm font-medium text-ivory-50 hover:bg-evergreen-700"
            >
              Setup Sheet
            </button>
            <button
              onClick={handleCreateShareLink}
              title="Get a link the couple can open to view this layout"
              className="rounded-lg bg-gold-400 px-3 py-1.5 font-body text-sm font-medium text-ink-700 hover:bg-gold-300"
            >
              Share with Couple
            </button>
          </div>
        </div>
      )}

      {!isFullScreen && shareUrl && (
        <div className="flex items-center gap-3 border-b border-gold-200 bg-gold-50 px-4 py-2">
          <p className="font-body text-sm text-ink-600">Share link ready:</p>
          <input
            readOnly
            value={shareUrl}
            onFocus={(e) => e.target.select()}
            className="flex-1 rounded border border-gold-300 bg-white px-2 py-1 font-body text-sm text-ink-600"
          />
          <button
            onClick={() => navigator.clipboard.writeText(shareUrl)}
            className="font-body text-sm text-evergreen-700 underline"
          >
            Copy
          </button>
          <button
            onClick={() => setShareUrl(null)}
            className="font-body text-sm text-ink-400 hover:text-ink-600"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {!isFullScreen && layoutMode === 'desktop' && (
          <div className="w-56 shrink-0 border-r border-ivory-300 bg-white">
            <ObjectLibraryPanel catalog={catalog} />
          </div>
        )}

        <div className="relative flex-1">
          <CanvasSizer room={roomDefinition} catalog={catalog} orientation={orientation} />

          {isFullScreen && (
            <button
              onClick={() => setIsFullScreen(false)}
              className="absolute left-4 top-4 rounded-lg bg-white/95 px-3 py-2 font-body text-sm text-ink-600 shadow-card hover:bg-white"
            >
              {'\u2715'} Exit focus mode
            </button>
          )}
        </div>

        {!isFullScreen && layoutMode === 'desktop' && (
          <div className="w-72 shrink-0 border-l border-ivory-300 bg-white">
            <InspectorPanel catalog={catalog} />
          </div>
        )}
      </div>

      {!isFullScreen && layoutMode === 'compact' && (
        <CompactPanels catalog={catalog} />
      )}

      {!isFullScreen && <LiveCountersBar guestCountTarget={wedding.guestCount} inventoryLimits={inventoryLimits} />}

      <ToastStack>
        {saveToast && (
          <Toast
            message={saveToast.message}
            tone={saveToast.tone}
            onDismiss={() => setSaveToast(null)}
          />
        )}
      </ToastStack>
    </div>
  );
}

/**
 * Measures its container and feeds explicit width/height into
 * PlannerCanvas — react-konva's Stage needs concrete pixel dimensions,
 * it can't size itself from CSS flex/percentage alone.
 */
function CanvasSizer({
  room,
  catalog,
  orientation,
}: {
  room: NonNullable<ReturnType<typeof useRoomDefinitions>['data']>[number];
  catalog: NonNullable<ReturnType<typeof useObjectCatalog>['data']>;
  orientation: RoomOrientation;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function measure() {
      if (!el) return;
      setSize({ width: el.clientWidth, height: el.clientHeight });
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full w-full">
      <PlannerCanvas room={room} catalog={catalog} width={size.width} height={size.height} orientation={orientation} />
    </div>
  );
}

/**
 * Tablet/touch layout: library and inspector become bottom sheets rather
 * than fixed side panels (PRD §14.4's responsive note). Library is a
 * collapsible drawer; inspector only appears once something is selected.
 */
function CompactPanels({
  catalog,
}: {
  catalog: NonNullable<ReturnType<typeof useObjectCatalog>['data']>;
}) {
  const [activeSheet, setActiveSheet] = useState<'none' | 'library' | 'inspector'>('none');
  const selectedIds = useEditorStore((state) => state.selectedIds);

  useEffect(() => {
    if (selectedIds.length > 0) setActiveSheet('inspector');
  }, [selectedIds]);

  return (
    <>
      <div className="flex border-t border-ivory-300 bg-white">
        <button
          onClick={() => setActiveSheet((s) => (s === 'library' ? 'none' : 'library'))}
          className="flex-1 py-2.5 font-body text-sm text-ink-600"
        >
          Add objects
        </button>
        <button
          onClick={() => setActiveSheet((s) => (s === 'inspector' ? 'none' : 'inspector'))}
          className="flex-1 border-l border-ivory-300 py-2.5 font-body text-sm text-ink-600"
        >
          {selectedIds.length > 0 ? 'Edit selected' : 'Inspector'}
        </button>
      </div>

      {activeSheet !== 'none' && (
        <div className="max-h-64 overflow-y-auto border-t border-ivory-300 bg-white">
          {activeSheet === 'library' ? (
            <ObjectLibraryPanel catalog={catalog} />
          ) : (
            <InspectorPanel catalog={catalog} />
          )}
        </div>
      )}
    </>
  );
}
